"use client";

import { useState } from "react";
import { Check, Copy } from "lucide-react";

interface Props {
  url: string;
  /** The title used in the WhatsApp share message body. */
  title: string;
  /** "full" shows labels next to icons; "compact" is icon-only with tooltips. */
  variant?: "full" | "compact";
  className?: string;
}

/**
 * Platform-default share row. Used on listings, developer profiles, and
 * anywhere else we want a consistent share UX.
 *
 * Three channels:
 *   • WhatsApp — diaspora's primary channel for family chats.
 *   • Facebook — diaspora groups; preview card built from og: tags on
 *     the target URL (requires og:title + og:description + og:image
 *     to look good, otherwise FB falls back to the bare URL).
 *   • Copy link — universal fallback. Powers Instagram (no web share
 *     intent), Threads, X, email, anywhere else.
 *
 * Tracking is intentionally NOT wired here so the component stays
 * generic. Callers that want analytics can wrap each button or use
 * the page-level PostHog auto-capture (clicks on anchors with
 * data-tracking attrs etc.).
 */
export function ShareRow({
  url,
  title,
  variant = "full",
  className = "",
}: Props) {
  const [copied, setCopied] = useState(false);

  const shareText = `${title} — ${url}`;
  const whatsappHref = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
  const facebookHref = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API can fail on http or denied permission. No-op:
      // the user can still right-click the address bar and copy.
    }
  };

  if (variant === "compact") {
    return (
      <div className={`inline-flex items-center gap-2 ${className}`}>
        <a
          href={whatsappHref}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Compartir por WhatsApp"
          title="Compartir por WhatsApp"
          className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#25D366] text-white transition hover:opacity-90"
        >
          <WhatsappGlyph className="h-4 w-4" />
        </a>
        <a
          href={facebookHref}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Compartir en Facebook"
          title="Compartir en Facebook"
          className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#1877F2] text-white transition hover:opacity-90"
        >
          <FacebookGlyph className="h-4 w-4" />
        </a>
        <button
          type="button"
          onClick={handleCopy}
          aria-label={copied ? "Enlace copiado" : "Copiar enlace"}
          title={copied ? "Enlace copiado" : "Copiar enlace"}
          className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-ink text-cream-50 transition hover:bg-ink-800"
        >
          {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
        </button>
      </div>
    );
  }

  return (
    <div className={`flex flex-wrap items-center gap-3 ${className}`}>
      <p className="text-sm font-medium text-ink-700">Compártelo:</p>
      <a
        href={whatsappHref}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-4 py-2 text-sm font-semibold text-white transition hover:opacity-90"
      >
        <WhatsappGlyph className="h-4 w-4" />
        WhatsApp
      </a>
      <a
        href={facebookHref}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 rounded-full bg-[#1877F2] px-4 py-2 text-sm font-semibold text-white transition hover:opacity-90"
      >
        <FacebookGlyph className="h-4 w-4" />
        Facebook
      </a>
      <button
        type="button"
        onClick={handleCopy}
        className="inline-flex items-center gap-2 rounded-full bg-ink px-4 py-2 text-sm font-semibold text-cream-50 transition hover:bg-ink-800"
      >
        {copied ? (
          <>
            <Check className="h-4 w-4" />
            Copiado
          </>
        ) : (
          <>
            <Copy className="h-4 w-4" />
            Copiar enlace
          </>
        )}
      </button>
    </div>
  );
}

function WhatsappGlyph({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden
      fill="currentColor"
      className={className}
    >
      <path d="M19.05 4.91A9.82 9.82 0 0 0 12.04 2c-5.46 0-9.91 4.45-9.91 9.91 0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38a9.9 9.9 0 0 0 4.74 1.21h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.91-7.01zm-7.01 15.24h-.01a8.21 8.21 0 0 1-4.19-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.22 8.22 0 0 1-1.26-4.38c0-4.54 3.7-8.23 8.25-8.23 2.2 0 4.27.86 5.83 2.42a8.18 8.18 0 0 1 2.41 5.82c0 4.54-3.7 8.23-8.24 8.23zm4.52-6.16c-.25-.12-1.47-.72-1.7-.81-.23-.08-.39-.12-.56.13-.16.25-.64.81-.79.97-.14.17-.29.19-.54.06-.25-.12-1.05-.39-2-1.23-.74-.66-1.23-1.47-1.38-1.72-.14-.25-.02-.39.11-.51.11-.11.25-.29.37-.43.12-.14.16-.25.25-.41.08-.17.04-.31-.02-.43-.06-.12-.56-1.34-.76-1.84-.2-.49-.41-.42-.56-.43h-.48c-.17 0-.43.06-.66.31-.23.25-.86.84-.86 2.06 0 1.21.88 2.38 1 2.55.12.17 1.74 2.66 4.22 3.73.59.25 1.05.4 1.41.51.59.19 1.13.16 1.56.1.48-.07 1.47-.6 1.67-1.18.21-.58.21-1.07.14-1.18-.06-.11-.23-.17-.48-.29z" />
    </svg>
  );
}

function FacebookGlyph({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden
      fill="currentColor"
      className={className}
    >
      <path d="M22 12.06C22 6.5 17.52 2 12 2S2 6.5 2 12.06c0 5 3.66 9.16 8.44 9.94v-7.03H7.9v-2.91h2.54V9.85c0-2.52 1.49-3.91 3.78-3.91 1.1 0 2.24.2 2.24.2v2.47h-1.26c-1.24 0-1.63.78-1.63 1.57v1.88h2.78l-.45 2.91h-2.33V22c4.78-.78 8.43-4.94 8.43-9.94z" />
    </svg>
  );
}
